import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t, hasChosenLang, getLang, setLang, SUPPORTED_LANGS } from '../../utils/creatorLang';
import { useCreatorAuth } from '../../utils/useCreatorAuth';
import LanguageSelector from '../../components/LanguageSelector';
import AvatarImg from '../../components/AvatarImg';
import {
  CreatorScreen, Card, Pill, Ring, TierBadge, DiamondGem, BottomNav,
  I, SectionLabel, StatTile, QuickLink,
} from '../../components/creator/primitives';

const SERVER_COLOR = { MENA: '#f97316', FR: '#3b82f6', IT: '#22c55e', DE: '#a855f7' };

const EVOLUTION_TIERS = [
  { pct: 4, days: 11, hours: 30 },
  { pct: 5, days: 15, hours: 40 },
  { pct: 6, days: 18, hours: 60 },
  { pct: 7, days: 22, hours: 80 },
];

const MAINTIEN_TIERS = [
  { pct: 2.5, days: 11, hours: 30 },
  { pct: 3.5, days: 15, hours: 40 },
  { pct: 4, days: 18, hours: 60 },
  { pct: 5, days: 22, hours: 80 },
];

const LANG_ORDER = ['en', 'fr', 'it', 'de'];

const fmtD = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(Math.round(n));
};
const fmtNum = (n) => (n == null ? '0' : Number(n).toLocaleString('en-US'));
const fmtStat = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
};
const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const { tiktokId: authTiktokId, loading: authLoading, isDebugMode } = useCreatorAuth();
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [managerName, setManagerName] = useState('—');
  const [lastDataDate, setLastDataDate] = useState(null);
  const [lastMonthData, setLastMonthData] = useState(null);
  const [last7Days, setLast7Days] = useState([]);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [lang, setLangState] = useState(getLang());

  useEffect(() => {
    if (!hasChosenLang()) setShowLangPicker(true);
  }, []);

  useEffect(() => {
    if (authLoading || !authTiktokId) return;
    const init = async () => {
      setLoading(true);
      try {
        const { data: creatorData } = await supabase
          .from('black_creators')
          .select('tiktok_id, tiktok_username, display_name, server, level, contract_start, contract_end_date, manager_id, tiktok_avatar_url, tiktok_display_name')
          .eq('tiktok_id', authTiktokId)
          .maybeSingle();

        // Session créateur sans dossier agence (non rattaché) → espace public, pas login.
        if (!creatorData) { navigate('/my/public-dashboard'); return; }
        setCreator(creatorData);

        if (creatorData.manager_id) {
          const { data: manager } = await supabase.from('profiles').select('name').eq('id', creatorData.manager_id).single();
          if (manager?.name) setManagerName(manager.name);
        }

        const { data: monthData } = await supabase
          .from('black_creator_current_month')
          .select('*, cashback_amount, cashback_pct')
          .eq('tiktok_id', authTiktokId)
          .single();
        setCurrentMonth(monthData || null);

        const { data: lastDaily } = await supabase
          .from('black_creator_daily')
          .select('date')
          .eq('tiktok_id', authTiktokId)
          .order('date', { ascending: false })
          .limit(1)
          .single();
        if (lastDaily?.date) setLastDataDate(lastDaily.date);

        const now = new Date();
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevPeriod = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
        const { data: prevData } = await supabase
          .from('black_creator_monthly')
          .select('diamonds, live_days, live_hours, tier_p, cashback')
          .eq('tiktok_id', authTiktokId)
          .eq('period', prevPeriod)
          .single();
        if (prevData) setLastMonthData(prevData);

        const { data: dailyData } = await supabase
          .from('black_creator_daily')
          .select('date, diamonds, live_duration_minutes')
          .eq('tiktok_id', authTiktokId)
          .order('date', { ascending: false })
          .limit(7);
        if (dailyData) setLast7Days(dailyData.reverse());
      } catch (err) {
        console.error('Dashboard init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [authTiktokId, authLoading]);

  const stats = useMemo(() => {
    if (!currentMonth) return { diamonds: 0, liveDays: 0, liveHours: 0 };
    return {
      diamonds: currentMonth.diamonds || 0,
      liveDays: currentMonth.live_days || 0,
      liveHours: parseFloat(((currentMonth.live_duration_minutes || 0) / 60).toFixed(1)),
    };
  }, [currentMonth]);

  const cashbackInfo = useMemo(() => {
    const tierCurrent = currentMonth?.tier_current || 0;
    const tierM1 = currentMonth?.tier_m1 || 0;
    const diamonds = stats.diamonds;
    const liveDays = stats.liveDays;
    const liveHours = stats.liveHours;

    let type = null;
    let tiers = [];

    const dayOfMonth = new Date().getDate();
    const isEarlyMonth = dayOfMonth <= 7 && tierCurrent <= 1 && tierM1 > 1 && diamonds < 50000;

    if (isEarlyMonth) { type = 'early_month'; tiers = EVOLUTION_TIERS; }
    else if (tierCurrent > tierM1) { type = 'evolution'; tiers = EVOLUTION_TIERS; }
    else if (tierCurrent === tierM1 && diamonds > 200000) { type = 'maintien'; tiers = MAINTIEN_TIERS; }
    else if (tierCurrent === tierM1) { type = 'push_maintien'; tiers = MAINTIEN_TIERS; }
    else { type = 'dropped'; tiers = EVOLUTION_TIERS; }

    let currentTierIndex = -1;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (liveDays >= tiers[i].days && liveHours >= tiers[i].hours) { currentTierIndex = i; break; }
    }

    const currentPct = currentTierIndex >= 0 ? tiers[currentTierIndex].pct : 0;
    const nextTier = currentTierIndex < tiers.length - 1 ? tiers[currentTierIndex + 1] : null;
    const maxTier = tiers[tiers.length - 1];

    let daysNeeded = 0, hoursNeeded = 0;
    if (nextTier) {
      daysNeeded = Math.max(0, nextTier.days - liveDays);
      hoursNeeded = Math.max(0, Math.ceil(nextTier.hours - liveHours));
    }

    const estimatedCashbackDiamonds = currentPct > 0 ? Math.round(diamonds * currentPct / 100) : 0;
    const maxCashbackDiamonds = Math.round(diamonds * maxTier.pct / 100);

    return { type, tiers, currentTierIndex, currentPct, nextTier, maxTier, daysNeeded, hoursNeeded, estimatedCashbackDiamonds, maxCashbackDiamonds, tierCurrent, tierM1 };
  }, [currentMonth, stats]);

  const objective = useMemo(() => {
    if (!currentMonth?.objective_diamonds || currentMonth.objective_diamonds <= 0) return null;
    const obj = currentMonth.objective_diamonds;
    const diamonds = stats.diamonds;
    const pct = Math.min(100, (diamonds / obj) * 100);
    const remaining = Math.max(0, obj - diamonds);
    return { obj, pct, remaining };
  }, [currentMonth, stats]);

  const cycleLang = () => {
    const idx = LANG_ORDER.indexOf(lang);
    const next = LANG_ORDER[(idx + 1) % LANG_ORDER.length];
    setLang(next);
    setLangState(next);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('creator_profile');
    navigate('/my/login');
  };

  if (loading) {
    return (
      <CreatorScreen>
        <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg-0)' }}>
          <div style={{
            width: 32, height: 32,
            border: '2px solid var(--accent-pink)', borderTopColor: 'transparent',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </CreatorScreen>
    );
  }

  if (!creator) return null;

  const qs = isDebugMode && authTiktokId ? `?creator=${authTiktokId}` : '';
  const locale = lang === 'fr' ? 'fr-FR' : lang === 'it' ? 'it-IT' : lang === 'de' ? 'de-DE' : 'en-US';
  const periodLabel = currentMonth?.period
    ? new Date(currentMonth.period + '-01').toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    : '—';
  const lastDataFormatted = lastDataDate ? new Date(lastDataDate).toLocaleDateString(locale) : null;
  const displayName = creator.tiktok_display_name || creator.username || '';
  const initials = displayName.slice(0, 2).toUpperCase();

  // Status pill for hero
  let statusPill;
  if (cashbackInfo.type === 'early_month') statusPill = <Pill color="cyan"><I.Clock size={11}/> {t('early_month')}</Pill>;
  else if (cashbackInfo.type === 'dropped') statusPill = <Pill color="red"><I.Alert size={11}/> {t('tier_dropped')}</Pill>;
  else if (cashbackInfo.type === 'evolution') statusPill = <Pill color="green"><I.TrendUp size={11}/> {t('evolution')}</Pill>;
  else if (cashbackInfo.type === 'maintien') statusPill = <Pill color="cyan"><I.Check size={11}/> {t('maintien')}</Pill>;
  else statusPill = <Pill color="gold"><I.Alert size={11}/> {t('not_eligible')}</Pill>;

  // Progress inside the ring = progress toward the next cashback tier (or 100% if already max)
  const ringValue = (() => {
    if (cashbackInfo.currentTierIndex >= cashbackInfo.tiers.length - 1) return 1;
    const next = cashbackInfo.nextTier;
    if (!next) return 0;
    const dayRatio = Math.min(1, stats.liveDays / next.days);
    const hourRatio = Math.min(1, stats.liveHours / next.hours);
    return (dayRatio + hourRatio) / 2;
  })();

  return (
    <CreatorScreen>
      <LanguageSelector
        show={showLangPicker}
        onDone={() => {
          setShowLangPicker(false);
          setLangState(getLang());
        }}
      />

      <div className="screen grain">
        {/* Debug banner */}
        {isDebugMode && (
          <div style={{
            margin: '12px 16px 0',
            background: 'oklch(0.65 0.24 25 / 0.08)',
            border: '1px solid oklch(0.65 0.24 25 / 0.25)',
            borderRadius: 10, padding: '8px 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 11, color: 'var(--accent-red)', fontWeight: 600 }}>
              {t('debug_mode')} — {creator.tiktok_id}
            </span>
            <button onClick={() => navigate('/admin/dashboard')} style={{
              fontSize: 11, color: 'var(--accent-red)', background: 'none',
              border: 'none', cursor: 'pointer', textDecoration: 'underline',
            }}>{t('back_admin')}</button>
          </div>
        )}

        {/* Header */}
        <div style={{ padding: '20px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, overflow: 'hidden',
              background: 'linear-gradient(135deg, var(--accent-pink), var(--accent-gold))',
              padding: 2, flexShrink: 0,
            }}>
              <div style={{
                width: '100%', height: '100%', borderRadius: 10,
                background: 'var(--bg-2)', display: 'grid', placeItems: 'center',
                overflow: 'hidden',
              }}>
                {creator.tiktok_avatar_url
                  ? <AvatarImg src={creator.tiktok_avatar_url} username={creator.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                  : <span style={{ color: 'var(--fg-0)', fontWeight: 800, fontSize: 14 }}>{initials}</span>}
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {/* hello + creator name */}
                Hi
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-0)', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                @{creator.username}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="tap" onClick={cycleLang} style={{
              height: 36, padding: '0 10px', borderRadius: 999,
              background: 'var(--bg-3)', border: '1px solid var(--line-1)',
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 11, color: 'var(--fg-1)', fontWeight: 600,
            }}>
              <I.Globe size={12} stroke="var(--fg-1)"/>
              {lang.toUpperCase()}
            </div>
            <div className="tap" onClick={handleLogout} style={{
              width: 36, height: 36, borderRadius: 12,
              background: 'var(--bg-3)', border: '1px solid var(--line-1)',
              display: 'grid', placeItems: 'center',
            }}>
              <I.Logout size={16} stroke="var(--fg-2)"/>
            </div>
          </div>
        </div>

        {/* Creator chips (server + level + manager) */}
        <div style={{ padding: '0 20px 8px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {creator.server && (
            <Pill color="neutral" style={{ background: `${SERVER_COLOR[creator.server] || '#6b7280'}22`, color: SERVER_COLOR[creator.server] || '#9ca3af' }}>
              {creator.server}
            </Pill>
          )}
          {creator.level && <Pill color="neutral">{creator.level}</Pill>}
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            Manager: <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{managerName}</span>
          </span>
        </div>

        {/* Hero — tier ring + diamonds */}
        <div style={{ padding: '12px 20px 0' }}>
          <div style={{
            position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(145deg, var(--bg-3) 0%, var(--bg-2) 70%)',
            border: '1px solid var(--line-2)',
            borderRadius: 28, padding: 20,
          }}>
            <div style={{
              position: 'absolute', top: -60, right: -60, width: 200, height: 200,
              background: 'radial-gradient(circle, var(--accent-pink-soft) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}/>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 600 }}>
                {periodLabel}
                {lastDataFormatted && <span style={{ color: 'var(--fg-4)', marginLeft: 6 }}>· {t('data_as_of')} {lastDataFormatted}</span>}
              </div>
              {statusPill}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 16, position: 'relative' }}>
              <Ring value={ringValue} size={110} stroke={9}
                color={cashbackInfo.type === 'dropped' ? 'var(--accent-red)' : 'var(--accent-pink)'}
                trackColor="rgba(255,255,255,0.06)">
                <TierBadge tier={cashbackInfo.tierCurrent || 1} size="lg"/>
              </Ring>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <DiamondGem size={20} color="var(--accent-cyan)"/>
                  <span className="mono tab" style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1.2, lineHeight: 1, color: 'var(--fg-0)' }}>
                    {fmtD(stats.diamonds)}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg-2)', marginTop: 4 }}>
                  {t('diamonds')}
                </div>
                {objective && (
                  <>
                    <div style={{ marginTop: 14, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${objective.pct}%`,
                        background: 'linear-gradient(90deg, var(--accent-pink), var(--accent-gold))',
                        borderRadius: 3, transition: 'width 0.8s',
                      }}/>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                      <span>{t('diamond_objective')}</span>
                      <span>{fmtD(stats.diamonds)} / {fmtD(objective.obj)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Next tier cashback hint */}
            {cashbackInfo.nextTier && (cashbackInfo.type === 'evolution' || cashbackInfo.type === 'maintien' || cashbackInfo.type === 'push_maintien') && (
              <div style={{
                marginTop: 14, padding: '10px 12px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--line-1)',
                borderRadius: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontSize: 12,
              }}>
                <span style={{ color: 'var(--fg-2)' }}>
                  {t('to_reach')} <span style={{ color: 'var(--fg-0)', fontWeight: 700 }}>{cashbackInfo.nextTier.pct}%</span>
                </span>
                <span className="mono" style={{ color: 'var(--accent-gold)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {cashbackInfo.daysNeeded > 0 && <span>+{cashbackInfo.daysNeeded}d</span>}
                  {cashbackInfo.hoursNeeded > 0 && <span>+{cashbackInfo.hoursNeeded}h</span>}
                  {cashbackInfo.daysNeeded === 0 && cashbackInfo.hoursNeeded === 0 && <span style={{ color: 'var(--accent-green)' }}>✓</span>}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Stat tiles */}
        <div style={{ padding: '14px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <StatTile label={t('live_days')} value={stats.liveDays} sub={null} icon={<I.Calendar size={14} stroke="var(--accent-cyan)"/>}/>
          <StatTile label={t('hours')} value={stats.liveHours} sub="h" icon={<I.Clock size={14} stroke="var(--accent-gold)"/>}/>
          <StatTile label={t('diamonds')} value={fmtD(stats.diamonds)} icon={<DiamondGem size={13} color="var(--accent-pink)"/>}/>
        </div>

        {/* Last 7 days chart */}
        {last7Days.length > 0 && (() => {
          const totalD = last7Days.reduce((s, d) => s + (d.diamonds || 0), 0);
          const totalH = parseFloat((last7Days.reduce((s, d) => s + (d.live_duration_minutes || 0), 0) / 60).toFixed(1));
          const maxD = Math.max(...last7Days.map(d => d.diamonds || 0), 1);
          return (
            <div style={{ padding: '14px 20px 0' }}>
              <Card pad={14}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600 }}>
                    {t('last_7_days')}
                  </span>
                  <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--fg-2)' }}>
                    <span>{fmtD(totalD)} <DiamondGem size={9} color="var(--accent-cyan)"/></span>
                    <span>{totalH}h</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 52 }}>
                  {last7Days.map((d, i) => {
                    const h = Math.max(6, ((d.diamonds || 0) / maxD) * 52);
                    const dayLabel = new Date(d.date).toLocaleDateString(locale, { weekday: 'short' }).slice(0, 2);
                    const hasActivity = (d.diamonds || 0) > 0;
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{
                          width: '100%', height: `${h}px`,
                          background: hasActivity
                            ? 'linear-gradient(180deg, var(--accent-pink), oklch(0.68 0.22 10 / 0.3))'
                            : 'rgba(255,255,255,0.06)',
                          borderRadius: 3,
                        }}/>
                        <span style={{ fontSize: 9, color: 'var(--fg-4)' }}>{dayLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          );
        })()}

        {/* Last month summary (conditional) */}
        {lastMonthData && (cashbackInfo.type === 'early_month' || stats.diamonds < 10000) && (
          <div style={{ padding: '14px 20px 0' }}>
            <Card pad={14} style={{ borderLeft: '3px solid var(--accent-cyan)' }}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>
                {t('last_month')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--fg-3)', marginBottom: 4 }}>{t('diamonds')}</div>
                  <div className="mono tab" style={{ fontSize: 16, fontWeight: 700 }}>{fmtD(lastMonthData.diamonds)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--fg-3)', marginBottom: 4 }}>{t('live_days')}</div>
                  <div className="mono tab" style={{ fontSize: 16, fontWeight: 700 }}>{lastMonthData.live_days || 0}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--fg-3)', marginBottom: 4 }}>{t('hours')}</div>
                  <div className="mono tab" style={{ fontSize: 16, fontWeight: 700 }}>{parseFloat((lastMonthData.live_hours || 0).toFixed(1))}h</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, fontSize: 10, color: 'var(--fg-3)' }}>
                <span>{t('last_month_tier')}: <span style={{ color: 'var(--accent-cyan)', fontWeight: 600 }}>T{lastMonthData.tier_p || '—'}</span></span>
                {lastMonthData.cashback > 0 && (
                  <span>{t('last_month_cashback')}: <span style={{ color: 'var(--accent-green)', fontWeight: 600 }}>{fmtD(lastMonthData.cashback)}</span></span>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Cashback card */}
        <div style={{ padding: '14px 20px 0' }}>
          <Card pad={16} style={{
            background: 'linear-gradient(145deg, var(--accent-green-soft), var(--bg-2))',
            border: '1px solid oklch(0.78 0.17 155 / 0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700 }}>
                {t('cashback')}
              </span>
              {cashbackInfo.type === 'evolution' && <Pill color="green"><I.TrendUp size={11}/> T{cashbackInfo.tierM1}→T{cashbackInfo.tierCurrent}</Pill>}
              {cashbackInfo.type === 'maintien' && <Pill color="cyan">T{cashbackInfo.tierCurrent} {t('maintien')}</Pill>}
              {cashbackInfo.type === 'push_maintien' && <Pill color="gold">{t('not_eligible')}</Pill>}
              {cashbackInfo.type === 'dropped' && <Pill color="red"><I.TrendDown size={11}/> T{cashbackInfo.tierM1}→T{cashbackInfo.tierCurrent}</Pill>}
              {cashbackInfo.type === 'early_month' && <Pill color="cyan"><I.Clock size={11}/> {t('early_month')}</Pill>}
            </div>

            <div style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 12, lineHeight: 1.4 }}>
              {cashbackInfo.type === 'evolution' && t('cashback_msg_evolution')}
              {cashbackInfo.type === 'maintien' && t('cashback_msg_maintien')}
              {cashbackInfo.type === 'push_maintien' && t('cashback_msg_push')}
              {cashbackInfo.type === 'dropped' && t('cashback_msg_dropped')}
              {cashbackInfo.type === 'early_month' && t('early_month_msg')}
            </div>

            {(cashbackInfo.type === 'evolution' || cashbackInfo.type === 'maintien') && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cashbackInfo.tiers.length}, 1fr)`, gap: 6, marginBottom: 12 }}>
                  {cashbackInfo.tiers.map((tier, i) => {
                    const isActive = i === cashbackInfo.currentTierIndex;
                    const isPassed = i < cashbackInfo.currentTierIndex;
                    const isFuture = i > cashbackInfo.currentTierIndex;
                    return (
                      <div key={i} style={{
                        padding: '10px 4px', borderRadius: 10,
                        background: isActive ? 'var(--accent-green-soft)' : isPassed ? 'oklch(0.78 0.17 155 / 0.05)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isActive ? 'oklch(0.78 0.17 155 / 0.4)' : 'var(--line-1)'}`,
                        textAlign: 'center', position: 'relative', opacity: isFuture ? 0.5 : 1,
                      }}>
                        {isActive && (
                          <div style={{
                            position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
                            fontSize: 8, padding: '1px 5px', borderRadius: 4,
                            background: 'var(--accent-green)', color: 'var(--bg-0)', fontWeight: 700, letterSpacing: 0.3,
                          }}>{t('current_label')}</div>
                        )}
                        <div className="mono tab" style={{ fontSize: 15, fontWeight: 700, color: isActive ? 'var(--accent-green)' : isPassed ? 'oklch(0.78 0.17 155 / 0.7)' : 'var(--fg-3)' }}>
                          {tier.pct}%
                        </div>
                        <div style={{ fontSize: 8, color: isActive ? 'var(--accent-green)' : 'var(--fg-4)', marginTop: 2 }}>
                          {tier.days}d · {tier.hours}h
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{
                  paddingTop: 10, borderTop: '1px solid var(--line-1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {t('estimated_cashback')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                      <span className="mono tab" style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent-green)' }}>
                        {fmtNum(cashbackInfo.estimatedCashbackDiamonds)}
                      </span>
                      <DiamondGem size={12} color="var(--accent-green)"/>
                    </div>
                  </div>
                  {cashbackInfo.currentPct < cashbackInfo.maxTier.pct && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {t('at_pct_would_be', { pct: cashbackInfo.maxTier.pct })}
                      </div>
                      <div className="mono tab" style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-3)', marginTop: 2 }}>
                        {fmtNum(cashbackInfo.maxCashbackDiamonds)}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {cashbackInfo.type === 'push_maintien' && (
              <div style={{
                background: 'var(--accent-gold-soft)',
                border: '1px solid oklch(0.82 0.14 85 / 0.25)',
                borderRadius: 10, padding: 10, textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, color: 'var(--accent-gold)', fontWeight: 700 }}>
                  {fmtD(Math.max(0, 200000 - stats.diamonds))} {t('diamonds_remaining')}
                </div>
                <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>
                  {t('to_unlock_maintien')}
                </div>
              </div>
            )}

            {cashbackInfo.type === 'dropped' && (
              <div style={{
                background: 'var(--accent-red-soft)',
                border: '1px solid oklch(0.65 0.24 25 / 0.25)',
                borderRadius: 10, padding: 10, textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, color: 'var(--accent-red)', fontWeight: 700 }}>
                  {t('tier_went_down', { m1: cashbackInfo.tierM1, cur: cashbackInfo.tierCurrent })}
                </div>
                <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>
                  {t('reach_objective')}
                </div>
              </div>
            )}

            {cashbackInfo.type === 'early_month' && (
              <div style={{
                background: 'var(--accent-cyan-soft)',
                border: '1px solid oklch(0.78 0.16 210 / 0.25)',
                borderRadius: 10, padding: 10, textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, color: 'var(--accent-cyan)', fontWeight: 700 }}>
                  T{cashbackInfo.tierM1} {t('previous_month')}
                </div>
                <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>
                  {t('early_month_msg')}
                </div>
              </div>
            )}

            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <Link to={`/my/rules${qs}`} style={{ fontSize: 11, color: 'var(--fg-3)', textDecoration: 'underline' }}>
                {t('view_rules')}
              </Link>
            </div>
          </Card>
        </div>

        {/* Quick links */}
        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel>Quick links</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <QuickLink
              tint="gold"
              icon={<I.Shield size={18} stroke="var(--accent-gold)"/>}
              title={t('cashback_rules')}
              desc={null}
              onClick={() => navigate(`/my/rules${qs}`)}
            />
            <QuickLink
              tint="red"
              icon={<I.Alert size={18} stroke="var(--accent-red)"/>}
              title={t('agency_rules')}
              desc={null}
              onClick={() => navigate(`/my/agency-rules${qs}`)}
            />
            <QuickLink
              tint="cyan"
              icon={<I.Book size={18} stroke="var(--accent-cyan)"/>}
              title={t('guidelines')}
              desc={null}
              onClick={() => navigate(`/my/guidelines${qs}`)}
            />
            <QuickLink
              tint="pink"
              icon={<I.Sword size={18} stroke="var(--accent-pink)"/>}
              title={t('match_request')}
              desc={null}
              onClick={() => navigate(`/my/match${qs}`)}
            />
          </div>
        </div>

        {/* TikTok stats */}
        {((creator.tiktok_follower_count || 0) + (creator.tiktok_following_count || 0) + (creator.tiktok_likes_count || 0) + (creator.tiktok_video_count || 0)) > 0 && (
          <div style={{ padding: '20px 20px 0' }}>
            <SectionLabel>TikTok</SectionLabel>
            <Card pad={14}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {[
                  ['Followers', creator.tiktok_follower_count],
                  ['Following', creator.tiktok_following_count],
                  ['Likes', creator.tiktok_likes_count],
                  ['Videos', creator.tiktok_video_count],
                ].map(([label, v]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div className="mono tab" style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-0)' }}>{fmtStat(v)}</div>
                    <div style={{ fontSize: 9, color: 'var(--fg-3)', marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Contract footer */}
        <div style={{ padding: '20px 20px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'var(--fg-4)' }}>
            {t('agency_contract')} · {formatDate(creator.contract_start)} → {formatDate(creator.contract_end)}
          </div>
        </div>
      </div>

      <BottomNav active="dashboard" qs={qs}/>
    </CreatorScreen>
  );
};

export default CreatorDashboard;
